// "Times in" selection. Precedence: URL tz > saved per-user preference >
// browser Local. A shared URL never overwrites the saved preference; only a
// deliberate selector choice saves. Preference state is keyed by authIdentity
// so a user switch never shows (or saves over) another user's value.
import { useCallback, useEffect, useRef, useState } from "react";
import { getPreference, setPreference } from "../../../data/preferencesClient.js";
import { TRAVEL_PREF_KEY, validTzChoice, resolveZone } from "./travelModel.js";

export function useTravelTimezone(authIdentity, urlTz, { onUrlChange } = {}) {
  const [pref, setPref] = useState({ identity: authIdentity, status: "loading", saved: null });
  const [save, setSave] = useState({ status: "idle", error: null });
  const seq = useRef(0);
  const identityRef = useRef(authIdentity);
  identityRef.current = authIdentity;
  const selectionSeq = useRef(0);
  const writes = useRef(Promise.resolve());
  const urlChangeRef = useRef(onUrlChange);
  urlChangeRef.current = onUrlChange;

  useEffect(() => {
    const mine = ++seq.current;
    const beforeSelection = selectionSeq.current;
    writes.current = Promise.resolve();
    setPref({ identity: authIdentity, status: "loading", saved: null });
    setSave({ status: "idle", error: null });
    Promise.resolve().then(() => getPreference(TRAVEL_PREF_KEY))
      .then((value) => { if (mine === seq.current && beforeSelection === selectionSeq.current) setPref({ identity: authIdentity, status: "ready", saved: validTzChoice(value) }); })
      .catch(() => { if (mine === seq.current && beforeSelection === selectionSeq.current) setPref({ identity: authIdentity, status: "error", saved: null }); });
    return () => { ++seq.current; };
  }, [authIdentity]);

  const current = pref.identity === authIdentity ? pref : { identity: authIdentity, status: "loading", saved: null };
  const choice = urlTz || (current.status === "loading" ? null : current.saved || "local");
  const source = urlTz ? "url" : current.saved ? "saved" : "default";

  const select = useCallback((value) => {
    const next = validTzChoice(value);
    if (!next || identityRef.current !== authIdentity) return;
    urlChangeRef.current?.(next);
    const mine = seq.current;
    const identity = authIdentity;
    const selected = ++selectionSeq.current;
    setPref({identity,status:"ready",saved:next});
    setSave({ status: "saving", error: null });
    writes.current = writes.current.catch(() => {}).then(() => {
      if (mine !== seq.current || identityRef.current !== identity) return;
      return setPreference(TRAVEL_PREF_KEY, next);
    })
      .then(() => {
        if (mine !== seq.current || selected !== selectionSeq.current) return;
        setPref({ identity, status: "ready", saved: next });
        setSave({ status: "saved", error: null });
      })
      .catch((err) => { if (mine === seq.current && selected === selectionSeq.current) setSave({ status: "error", error: err?.message || "Could not save your time zone." }); });
  }, [authIdentity]);

  return {
    choice, // null while the saved preference is still loading (and no URL tz)
    zone: choice ? resolveZone(choice) : null,
    localUnavailable: choice === "local" && !resolveZone("local"),
    source,
    prefStatus: current.status,
    saveState: save,
    select,
  };
}
